import { useState } from "react";

import { NavLink, Outlet, useLocation } from "react-router-dom";

import { LogOut, Menu } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Logo, Wordmark } from "@/components/shared/logo";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useLogout } from "@/hooks/use-session";
import { displayName } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth";

import { HeaderControls } from "./header-controls";
import { NAV_SECTIONS, isActive } from "./nav-items";

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-3">
      <Logo className="size-8" />
      <Wordmark className="text-lg" />
      <span className="bg-sidebar-primary/20 text-sidebar-primary rounded-md px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase">
        admin
      </span>
    </div>
  );
}

/**
 * Menyu. Faol belgi (`layoutId`) bir bo'limdan ikkinchisiga suzib o'tadi —
 * qayerdan qayerga o'tilgani ko'z bilan kuzatiladi. Mobil menyuda boshqa
 * `layoutId`: ikki ro'yxat bir belgini talashmasin.
 */
function Navigation({ onNavigate, layoutId }: { onNavigate?: () => void; layoutId: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <nav className="flex flex-col gap-5">
      {NAV_SECTIONS.map(section => (
        <div key={section.titleKey ?? "main"} className="space-y-1">
          {section.titleKey ? (
            <p className="text-sidebar-foreground/45 px-3 pb-1 text-[11px] font-medium tracking-wider uppercase">
              {t(section.titleKey)}
            </p>
          ) : null}
          {section.items.map(item => {
            const active = isActive(pathname, item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={onNavigate}
                className={cn(
                  "relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
                  active
                    ? "text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/40",
                )}
              >
                {active ? (
                  <motion.span
                    layoutId={layoutId}
                    className="bg-sidebar-accent absolute inset-0 rounded-xl"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  >
                    <span className="bg-sidebar-primary absolute inset-y-2 left-0 w-1 rounded-full" />
                  </motion.span>
                ) : null}
                <item.icon
                  className={cn("relative size-4", active && "text-sidebar-primary")}
                  aria-hidden
                />
                <span className="relative">{t(item.labelKey)}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function SidebarUser() {
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);
  const logout = useLogout();
  if (!user) return null;
  const name = displayName(user);
  return (
    <div className="bg-sidebar-accent/50 flex items-center gap-3 rounded-2xl p-2.5">
      <UserAvatar name={name} seed={user.id} className="size-9" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{name}</p>
        <p className="text-sidebar-foreground/55 truncate text-xs">
          {user.telegramUsername ? `@${user.telegramUsername}` : user.telegramUserId}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t("common.logout")}
        onClick={() => void logout()}
        className="text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
      >
        <LogOut />
      </Button>
    </div>
  );
}

export function AppLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="bg-background flex h-svh overflow-hidden">
      <aside className="bg-sidebar text-sidebar-foreground hidden h-svh w-64 shrink-0 flex-col gap-7 overflow-y-auto px-3 py-5 lg:flex">
        <Brand />
        <div className="flex-1">
          <Navigation layoutId="nav-active" />
        </div>
        <SidebarUser />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="bg-sidebar text-sidebar-foreground w-72 gap-6 border-none p-4"
        >
          <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
          <Brand />
          <Navigation layoutId="nav-active-mobile" onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="bg-background/80 z-20 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label={t("nav.menu")}
            onClick={() => setMobileOpen(true)}
          >
            <Menu />
          </Button>
          <div className="flex items-center gap-2 lg:hidden">
            <Logo className="size-7" />
            <Wordmark />
          </div>
          <div className="flex-1" />
          <HeaderControls />
        </header>
        {/* Sahifa almashganda kontent pastdan yumshoq ko'tariladi.
            Qobiq ekran balandligida qotgan: jadval sahifalari qolgan joyni
            to'liq egallaydi va faqat jadval ichi aylanadi. Oddiy sahifalar
            (umumiy ko'rinish, profil) shu `main` ning o'zida aylanadi. */}
        <motion.main
          key={pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="thin-scrollbar flex min-h-0 w-full flex-1 flex-col gap-6 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
